// src/components/ElencoNF.js
// Elenco delle richieste NF per chi le fa (W435) — lo stesso nella pagina «Richiedi NF» e
// nella scheda della commessa (prima erano due copie). Ogni riga mostra lo stato vero
// (Bozza, Da gestire, In gestione, Ordinata, Consegnata, Annullata), la descrizione con i suoi
// a capo e, se l'ordine è diverso dalla richiesta, cosa è cambiato. Un clic apre il dettaglio.
import React, { useState } from "react";
import { deleteDoc, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { Printer, Trash2 } from "lucide-react";
import { statoNf, nfModificabileDa, nfEliminabileDa, etichettaNfOrdinata, testoDifferenze, haOdaValido, articoliValidi } from "../utils/acquisti";
import { stampaNF } from "../utils/stampaNF";
import DettaglioNF, { BadgeStatoNF } from "./DettaglioNF";

export async function eliminaNf(nf, utente) {
  // Mai inviata: si cancella. Già inviata (ha un numero): resta traccia, ma sparisce da tutti gli elenchi.
  if (statoNf(nf) === "Bozza") await deleteDoc(doc(db, "nuoveForniture", nf.id));
  else await updateDoc(doc(db, "nuoveForniture", nf.id), { eliminata: true, eliminataDa: utente?.uid || "", eliminataAt: serverTimestamp(), updatedAt: serverTimestamp() });
}

export default function ElencoNF({ nfs, orgId, currentUser, onModifica, mostraCommessa = true, vuoto = null }) {
  const { addToast } = useToast();
  const [apertaId, setApertaId] = useState(null);
  const visibili = nfs.filter(n => !n.eliminata);
  const aperta = visibili.find(n => n.id === apertaId) || null;
  const elimina = async (nf) => {
    const bozza = statoNf(nf) === "Bozza";
    if (!await confirm(bozza ? "Eliminare questa bozza?" : `Eliminare la NF #${nf.numero || ""}? Nessuno dell'ufficio Acquisti l'ha ancora presa in gestione.`, { type: "danger" })) return;
    try { await eliminaNf(nf, currentUser); setApertaId(null); addToast(bozza ? "Bozza eliminata" : "NF eliminata", "info"); }
    catch (e) { addToast(messaggioErrore(e, "eliminare la NF"), "error"); }
  };
  if (!visibili.length) return vuoto;
  return (
    <div>
      {visibili.map(nf => {
        const ordine = etichettaNfOrdinata(nf);
        const diff = haOdaValido(nf) ? testoDifferenze(nf.odaDifferenze) : "";
        const nArt = articoliValidi(nf.articoli).length;
        return (
          <div key={nf.id} role="button" tabIndex={0} onClick={() => setApertaId(nf.id)} onKeyDown={e => e.key === "Enter" && setApertaId(nf.id)}
            style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 16px", marginBottom: 10, background: nf.annullata ? "#fafafa" : "#fff", display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", opacity: nf.annullata ? .75 : 1 }}>
            {nf.numero && <div style={{ fontFamily: "monospace", fontWeight: 800, color: "#7c3aed", fontSize: 14, flexShrink: 0, paddingTop: 1 }}>#{nf.numero}</div>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", whiteSpace: "pre-line", display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{nf.descrizione}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                {mostraCommessa ? <>{nf.codiceCommessa && <span style={{ color: "#7c3aed", marginRight: 4 }}>[{nf.codiceCommessa}]</span>}{nf.commessaNome || "—"}</> : <>da {nf.compilatoreNome || "—"}</>}
                {nArt > 0 && <span style={{ marginLeft: 8 }}>· {nArt} articol{nArt === 1 ? "o" : "i"}</span>}
                {nf.urgenza === "Urgente" && <span style={{ marginLeft: 8, color: "#dc2626", fontWeight: 700 }}>🚨 Urgente</span>}
              </div>
              {nf.presaInCaricoDa && !ordine && !nf.annullata && <div style={{ fontSize: 10.5, color: "#7c3aed", marginTop: 2 }}>In gestione da {nf.presaInCaricoDaNome || "l'ufficio Acquisti"} — per cambiare qualcosa scrivi nella chat della NF</div>}
              {nf.rdoRiepilogo && !ordine && !nf.annullata && <div style={{ fontSize: 10.5, color: "#b45309", marginTop: 2 }}>📨 L'ufficio acquisti sta chiedendo le offerte: {nf.rdoRiepilogo}</div>}
              {ordine && <div style={{ fontSize: 10.5, color: "#059669", fontWeight: 700, marginTop: 2 }}>🛒 {ordine}</div>}
              {diff && <div style={{ fontSize: 10.5, color: "#b45309", fontWeight: 600, marginTop: 2 }}>⚠️ Ordine diverso dalla richiesta: {diff}</div>}
              {nf.annullata && <div style={{ fontSize: 10.5, color: "#6b7280", marginTop: 2 }}>Annullata{nf.annullataDaNome ? ` da ${nf.annullataDaNome}` : ""}: {nf.motivoAnnullamento || "—"}</div>}
              {nfModificabileDa(nf, currentUser?.uid) && statoNf(nf) === "Da gestire" && <div style={{ fontSize: 10.5, color: "#6b7280", marginTop: 2 }}>Puoi ancora modificarla: nessuno dell'ufficio Acquisti l'ha presa in gestione</div>}
            </div>
            <BadgeStatoNF nf={nf} stile={{ flexShrink: 0 }}/>
            <button onClick={e => { e.stopPropagation(); stampaNF(nf); }} title="Stampa / PDF" aria-label="Stampa / PDF"
              style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer", color: "#1d4ed8", padding: "5px 8px", display: "flex", alignItems: "center", flexShrink: 0 }}>
              <Printer size={13}/>
            </button>
            {nfEliminabileDa(nf, currentUser?.uid) && (
              <button onClick={e => { e.stopPropagation(); elimina(nf); }} title="Elimina" aria-label="Elimina" style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5", padding: 4, flexShrink: 0 }}>
                <Trash2 size={14}/>
              </button>
            )}
          </div>
        );
      })}
      {aperta && (
        <DettaglioNF nf={aperta} orgId={orgId} currentUser={currentUser} onChiudi={() => setApertaId(null)}
          onModifica={nfModificabileDa(aperta, currentUser?.uid) && onModifica ? (n) => { setApertaId(null); onModifica(n); } : null}
          onElimina={nfEliminabileDa(aperta, currentUser?.uid) ? elimina : null}/>
      )}
    </div>
  );
}
